// Spec: .github/specs/361623-global-banners-fe.md (NAV-1–NAV-8)
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  HostListener,
  AfterViewChecked,
  NgZone,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  catchError,
  forkJoin,
  of,
  takeUntil,
  Subject,
  map,
  filter,
  distinctUntilChanged,
} from 'rxjs';
import { RenderLabelPipe } from '../../../pipes/render-label/render-label.pipe';
import { endPoints } from 'src/app/common/constants/endpoints';
import { ApiService } from 'src/app/common/services/api-service/api-service';
import { SessionStorageUtility } from 'src/app/common/utils/sessionStorageUtils';
import { CommonUtility } from 'src/app/common/utils/commonUtils';
import { LoginService } from 'src/app/common/services/login-service/login.service';
import { ROUTER_CONSTANTS } from 'src/app/common/constants/router.constants';
import { DashboardService } from 'src/app/modules/dashboard/services/dashboard.service';
import {
  USER_TYPES,
  DASHBOARD_TAB_CONSTANTS,
  TAB_TO_LOCATION_MAP,
} from 'src/app/common/constants/global.constant';
import {
  GlobalBannerReadApiResponse,
  GlobalBannersByLocationResponse,
  LocationBasedBannerRecord,
} from 'src/app/modules/admin/interfaces/admin';
import { SESSION_STORAGE_KEYS } from 'src/app/common/constants/storage.constant';

interface BannerItem {
  attributes: { type: string };
  type__c: string;
  content__c: string;
  id: string;
  dismissed?: boolean;
}

interface BannerResponse {
  records: BannerItem[];
}

interface BannerGroup {
  type: string;
  banners: BannerItem[];
  start: number;
}

@Component({
  selector: 'app-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RenderLabelPipe, CommonModule],
})
export class BannerComponent implements OnInit, OnDestroy, AfterViewChecked {
  banners: BannerItem[] = [];
  groups: BannerGroup[] = [];
  availableBanners: BannerItem[] = [];
  activeIndex = 0;
  areaDismissed = false;
  isCollapsed = false;
  loading = false;
  private requestVersion = 0;
  private readonly dismissedLocations = new Set<string>();
  private lastPageFocus: HTMLElement | null = null;
  private readonly typeOrder = ['Danger', 'Warning', 'Info', 'Success'];
  private focusFrame: number | null = null;
  private sizingFrame: number | null = null;
  private sizingObserver: ResizeObserver | null = null;
  private sizingPanel: HTMLElement | null = null;
  private sizingSignature = '';
  private sizingWidth = 0;
  private destroy$ = new Subject<void>();
  private readonly BANNER_SESSION_FLAG = 'bannerSessionFlag';
  private readonly BANNER_HARD_REFRESH_FLAG = 'bannerHardRefreshFlag';
  private readonly BANNER_CACHE_KEY = 'bannerCache_all';
  private readonly EVERYONE_BANNER_CACHE_KEY = 'bannerCache_everyone';
  private currentLocation: string = DASHBOARD_TAB_CONSTANTS.CM;
  private allBannersCache: LocationBasedBannerRecord[] = [];
  private everyoneBanners: BannerItem[] = [];

  constructor(
    public readonly router: Router,
    public contentPipe: RenderLabelPipe,
    public readonly elementRef: ElementRef,
    private readonly apiService: ApiService,
    private readonly loginService: LoginService,
    private readonly dashboardService: DashboardService,
    private readonly zone: NgZone,
  ) {}

  ngOnInit(): void {
    // The component lives at the app root, so it is created before login completes.
    this.loginService.isLoggedInSub
      .pipe(
        distinctUntilChanged(),
        filter((isLoggedIn) => isLoggedIn),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.initializeBanners());
    this.watchForTabChanges();
    this.watchForRouteChanges();
  }

  ngOnDestroy(): void {
    this.requestVersion++;
    this.sizingObserver?.disconnect();
    if (this.sizingFrame !== null) {
      cancelAnimationFrame(this.sizingFrame);
    }
    if (this.focusFrame !== null) {
      cancelAnimationFrame(this.focusFrame);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Refit after rendering a new message or changing the available controls. */
  ngAfterViewChecked(): void {
    const panel = this.elementRef.nativeElement.querySelector(
      '.banner-container',
    ) as HTMLElement | null;
    if (panel !== this.sizingPanel) {
      this.sizingObserver?.disconnect();
      this.sizingPanel = panel;
      this.sizingSignature = '';
      this.sizingWidth = 0;
      if (panel) {
        this.zone.runOutsideAngular(() => {
          this.sizingObserver = new ResizeObserver(([entry]) => {
            // Font changes alter height; only width changes require another fit.
            if (entry && entry.contentRect.width !== this.sizingWidth) {
              this.sizingWidth = entry.contentRect.width;
              this.scheduleFontFit();
            }
          });
          this.sizingObserver.observe(panel);
        });
      }
    }
    const signature = JSON.stringify([
      this.activeBanner?.content__c,
      this.activeIndex,
      this.isCollapsed,
      this.groups.map((group) => [group.type, group.banners.length]),
    ]);
    if (panel && signature !== this.sizingSignature) {
      this.sizingSignature = signature;
      this.scheduleFontFit();
    }
  }

  /** Refit when viewport height or width changes, including browser zoom. */
  @HostListener('window:resize')
  onViewportResize(): void {
    this.scheduleFontFit();
  }

  /** Coalesce measurements outside Angular to avoid change-detection loops. */
  private scheduleFontFit(): void {
    if (this.sizingFrame !== null || !this.sizingPanel) {
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.sizingFrame = requestAnimationFrame(() => {
        this.sizingFrame = null;
        if (this.sizingPanel) {
          this.fitBannerFont(this.sizingPanel);
        }
      });
    });
  }

  /**
   * Reduce text from 14px to no smaller than 12px if natural height exceeds 30%.
   * @param panel Rendered banner shell, including its controls and message.
   */
  private fitBannerFont(panel: HTMLElement): void {
    const controls = panel.querySelector<HTMLElement>('.banner-controls');
    const message = panel.querySelector<HTMLElement>('.banner-message');
    if (!controls || !message || window.innerHeight <= 0) {
      return;
    }
    const heightBudget = window.innerHeight * 0.3;
    // Measure scrollHeight, not the capped panel height, so overflow also counts.
    // Always start at the default so a shorter message restores normal text.
    for (let fontSize = 14; fontSize >= 12; fontSize -= 0.5) {
      panel.style.setProperty('--banner-font-size', `${fontSize}px`);
      const naturalHeight = controls.scrollHeight + message.scrollHeight + 2;
      if (naturalHeight <= heightBudget) {
        break;
      }
    }
  }

  /**
   * Remember page focus so dismissal does not strand focus in a removed panel.
   * @param event Document focus event.
   */
  @HostListener('document:focusin', ['$event'])
  onPageFocus(event: FocusEvent): void {
    const target = event.target;
    if (
      target instanceof HTMLElement &&
      !this.elementRef.nativeElement.contains(target)
    ) {
      this.lastPageFocus = target;
    }
  }

  get activeBanner(): BannerItem | undefined {
    return this.availableBanners[this.activeIndex];
  }

  get activeGroup(): BannerGroup | undefined {
    return this.groups.find(
      (group) => group.type === this.activeBanner?.type__c,
    );
  }

  get typePosition(): number {
    return this.activeGroup ? this.activeIndex - this.activeGroup.start + 1 : 0;
  }

  /**
   * Select the first message of the requested nonempty type.
   * @param type Banner type to select.
   */
  selectType(type: string): void {
    const group = this.groups.find((item) => item.type === type);
    if (group) {
      this.activeIndex = group.start;
    }
  }

  /**
   * Navigate the complete list, wrapping across type boundaries.
   * @param direction One for next or minus one for previous.
   */
  navigate(direction: number): void {
    const total = this.availableBanners.length;
    if (total > 1) {
      this.activeIndex = (this.activeIndex + direction + total) % total;
    }
  }

  /** Collapse the banner panel to its toggle button or expand it again. */
  toggleCollapsed(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  /** Dismiss the entire panel for the current location, independent of message IDs. */
  dismissBannerArea(): void {
    const restoreFocus = this.elementRef.nativeElement.contains(
      document.activeElement,
    );
    this.areaDismissed = true;
    this.dismissedLocations.add(this.currentLocation);
    try {
      SessionStorageUtility.set(
        `bannerAreaDismissed_${this.currentLocation}`,
        true,
      );
    } catch {
      // In-memory dismissal still works when browser storage is unavailable.
    }
    if (restoreFocus) {
      this.restorePageFocus();
    }
  }

  /** Restore a reachable page target when the focused panel is removed. */
  private restorePageFocus(): void {
    const target = this.lastPageFocus?.isConnected
      ? this.lastPageFocus
      : Array.from(
          document.querySelectorAll<HTMLElement>(
            'main, [role="main"], a[href], button',
          ),
        ).find((element) => !this.elementRef.nativeElement.contains(element));
    if (target) {
      if (
        !target.hasAttribute('tabindex') &&
        target.matches('main, [role="main"]')
      ) {
        target.setAttribute('tabindex', '-1');
      }
      target.focus();
    }
  }

  /**
   * Preserve type button DOM identity through API refreshes.
   * @param index Group index supplied by Angular.
   * @param group Current type group.
   * @returns The unique type key.
   */
  trackByGroupType(index: number, group: BannerGroup): string {
    return group.type;
  }

  private watchForTabChanges(): void {
    this.dashboardService.selectedTab$
      .pipe(
        filter((tabLabel): tabLabel is string => !!tabLabel),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.refreshActiveLocation());
  }

  private watchForRouteChanges(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.refreshActiveLocation());
  }

  private refreshActiveLocation(): void {
    const resolvedLocation = this.resolveCurrentLocation();
    if (resolvedLocation !== this.currentLocation) {
      this.setActiveLocation(resolvedLocation);
    }
  }

  private resolveCurrentLocation(): string {
    const path = this.getCleanPath(this.getCurrentPathname());
    const selectedTab = SessionStorageUtility.get(
      SESSION_STORAGE_KEYS.selectedDashboardTab,
    );

    if (this.isDashboardRoute(path) && selectedTab) {
      return selectedTab;
    }

    const urlLocation = this.resolveLocationFromPath(path);
    if (urlLocation) {
      return urlLocation;
    }

    return selectedTab || DASHBOARD_TAB_CONSTANTS.CM;
  }

  private getCurrentPathname(): string {
    return window.location.pathname;
  }

  private isDashboardRoute(path: string): boolean {
    return path.includes(`/${ROUTER_CONSTANTS.dashboard}`);
  }

  private getCleanPath(url: string): string {
    return (url || '').split('?')[0].split('#')[0];
  }

  private resolveLocationFromPath(path: string): string | null {
    if (path.includes(`/${ROUTER_CONSTANTS.viewAccount}/`)) {
      return DASHBOARD_TAB_CONSTANTS.ACCOUNT;
    }
    if (path.includes(`/${ROUTER_CONSTANTS.detail}/`)) {
      return DASHBOARD_TAB_CONSTANTS.CM;
    }
    if (path.includes('/ufa/')) {
      return DASHBOARD_TAB_CONSTANTS.UFA;
    }
    if (path.includes(`/${ROUTER_CONSTANTS.cxProfile}/`)) {
      return DASHBOARD_TAB_CONSTANTS.CX_PROFILE;
    }
    return null;
  }

  @HostListener('window:keydown', ['$event'])
  onWindowKeydown(event: KeyboardEvent): void {
    if (this.isHardRefreshShortcut(event)) {
      SessionStorageUtility.set(this.BANNER_HARD_REFRESH_FLAG, true);
    }
  }

  private initializeBanners(): void {
    try {
      this.currentLocation = this.resolveCurrentLocation();

      const sessionBannerFlag = SessionStorageUtility.get(
        this.BANNER_SESSION_FLAG,
      );
      const hardRefreshFlag = SessionStorageUtility.get(
        this.BANNER_HARD_REFRESH_FLAG,
      );
      const shouldFetchBanners = !sessionBannerFlag || !!hardRefreshFlag;

      if (shouldFetchBanners) {
        try {
          SessionStorageUtility.clearItem(this.BANNER_HARD_REFRESH_FLAG);
          this.clearAllDismissedFlags();
        } catch {
          // Continue with getBanners even if session storage set fails
        }
        this.getBanners();
        return;
      }
      this.loadCachedBanners();
    } catch {
      this.getBanners();
    }
  }

  private isHardRefreshShortcut(event: KeyboardEvent): boolean {
    const key = event.key?.toLowerCase();
    const ctrlF5 = key === 'f5' && event.ctrlKey;
    const ctrlShiftR = key === 'r' && event.ctrlKey && event.shiftKey;
    const cmdShiftR = key === 'r' && event.metaKey && event.shiftKey;
    return ctrlF5 || ctrlShiftR || cmdShiftR;
  }

  getBanners(): void {
    const version = ++this.requestVersion;
    let failures = 0;
    this.loading = true;
    const locationBannerRequest = this.apiService
      .callGetApi<GlobalBannersByLocationResponse>(
        endPoints.casemgmt.getGlobalBannersByLocation.URI,
      )
      .pipe(
        map((response: GlobalBannersByLocationResponse) =>
          this.mapLocationBasedBannerResponse(response),
        ),
        catchError(() => {
          failures++;
          return of([] as LocationBasedBannerRecord[]);
        }),
      );
    const everyoneBannerRequest = this.apiService
      .callGetApi<GlobalBannerReadApiResponse>(
        endPoints.casemgmt.getGlobalBannerData.URI,
      )
      .pipe(
        map((response: GlobalBannerReadApiResponse) =>
          this.mapGlobalBannerResponse(response),
        ),
        catchError(() => {
          failures++;
          return of([] as BannerItem[]);
        }),
      );

    const legacyUpsBannerRequest = this.isUPSUser()
      ? this.apiService
          .callGetApi<BannerResponse>(endPoints.casemgmt.getBannerData.URI)
          .pipe(
            map((response: BannerResponse) =>
              this.mapLegacyBannerResponse(response),
            ),
            catchError(() => {
              failures++;
              return of([] as BannerItem[]);
            }),
          )
      : of([] as BannerItem[]);

    forkJoin([
      locationBannerRequest,
      everyoneBannerRequest,
      legacyUpsBannerRequest,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([locationBanners, everyoneBanners, legacyBanners]) => {
        if (version !== this.requestVersion) {
          return;
        }
        this.loading = false;
        if (failures === (this.isUPSUser() ? 3 : 2)) {
          return;
        }
        try {
          SessionStorageUtility.set(this.BANNER_SESSION_FLAG, true);
        } catch {
          // Storage failures must not prevent rendering successful API data.
        }
        this.allBannersCache = locationBanners;
        this.everyoneBanners = everyoneBanners;
        this.cacheAllBanners(locationBanners);
        this.cacheEveryoneBanners(everyoneBanners);
        this.updateFilteredBanners(
          locationBanners.length === 0 ? legacyBanners : [],
        );
      });
  }

  private mapLocationBasedBannerResponse(
    response: GlobalBannersByLocationResponse,
  ): LocationBasedBannerRecord[] {
    if (!response?.records || !Array.isArray(response.records)) {
      return [];
    }

    return response.records.map((record) => ({
      type: record.type || 'Info',
      displayLocations: record.displayLocations || [],
      content: record.content || '',
    }));
  }

  private mapLegacyBannerResponse(response: BannerResponse): BannerItem[] {
    return (response?.records || []).map((banner) => ({
      ...banner,
      id: `legacy-banner-${Date.now()}`,
      dismissed: false,
    }));
  }

  private mapGlobalBannerResponse(
    response: GlobalBannerReadApiResponse,
  ): BannerItem[] {
    const message = response?.message || '';
    const content = message?.trim();
    const shown = this.resolveShownFlag(response);
    if (!shown || !content) {
      return [];
    }

    return [
      {
        attributes: { type: 'Global_Banner__c' },
        type__c: 'Danger',
        content__c: content,
        id: `global-banner-${Date.now()}`,
        dismissed: false,
      },
    ];
  }

  private resolveShownFlag(response: GlobalBannerReadApiResponse): boolean {
    const shownValue = response?.shown;
    if (typeof shownValue === 'string') {
      return shownValue.toLowerCase() === 'true';
    }
    return !!shownValue;
  }

  private isUPSUser(): boolean {
    const userType = this.loginService.userProfile?.userDetails?.userType;
    return userType?.toLowerCase() === USER_TYPES.upser;
  }

  private cacheAllBanners(banners: LocationBasedBannerRecord[]): void {
    try {
      SessionStorageUtility.set(this.BANNER_CACHE_KEY, JSON.stringify(banners));
    } catch {
      // Storage failures must not prevent rendering successful API data.
    }
  }

  private cacheEveryoneBanners(banners: BannerItem[]): void {
    try {
      SessionStorageUtility.set(
        this.EVERYONE_BANNER_CACHE_KEY,
        JSON.stringify(banners),
      );
    } catch {
      // Storage failures must not prevent rendering successful API data.
    }
  }

  private loadCachedBanners(): void {
    const cached = SessionStorageUtility.get(this.BANNER_CACHE_KEY);
    const cachedEveryoneBanners = SessionStorageUtility.get(
      this.EVERYONE_BANNER_CACHE_KEY,
    );

    this.allBannersCache = cached ? JSON.parse(cached) : [];
    this.everyoneBanners = cachedEveryoneBanners
      ? JSON.parse(cachedEveryoneBanners)
      : [];
    this.updateFilteredBanners();
  }

  private updateFilteredBanners(
    legacyBanners: BannerItem[] = [],
    resetSelection = false,
  ): void {
    const displayLocations = this.getDisplayLocationsForCurrentTab();
    const dismissedBannerIds = this.getDismissedBannerIds(this.currentLocation);

    const locationBanners = this.allBannersCache
      .filter((record) =>
        record.displayLocations.some((location) =>
          displayLocations.includes(location),
        ),
      )
      .map((record, index) => {
        const bannerId = this.generateBannerId(record, index);
        return {
          attributes: { type: record.type },
          type__c: record.type,
          content__c: record.content,
          id: bannerId,
          dismissed: dismissedBannerIds.includes(bannerId),
        };
      });

    this.banners = [
      ...locationBanners,
      ...this.everyoneBanners.map((banner) => ({
        ...banner,
        dismissed: dismissedBannerIds.includes(banner.id),
      })),
      ...legacyBanners.map((banner) => ({
        ...banner,
        dismissed: dismissedBannerIds.includes(banner.id),
      })),
    ];
    this.rebuildNavigation(resetSelection);
  }

  /**
   * Reconcile counts and selection in one snapshot after source/location changes.
   * @param resetSelection
   */
  private rebuildNavigation(resetSelection = false): void {
    const previous = this.activeBanner;
    const previousPosition = this.typePosition - 1;
    const occurrence = previous
      ? this.availableBanners
          .slice(0, this.activeIndex)
          .filter(
            (banner) =>
              banner.type__c === previous.type__c &&
              banner.content__c === previous.content__c,
          ).length
      : 0;
    const panelFocused = this.elementRef.nativeElement.contains(
      document.activeElement,
    );
    const focusedType = panelFocused
      ? document.activeElement?.getAttribute('data-banner-type')
      : null;
    const grouped = new Map<string, BannerItem[]>();
    this.getVisibleBanners().forEach((banner) => {
      if (!banner.content__c?.trim()) {
        return;
      }
      const rawType = banner.type__c?.trim() || 'Info';
      const type =
        this.typeOrder.find(
          (known) => known.toLowerCase() === rawType.toLowerCase(),
        ) || rawType;
      const records = grouped.get(type) || [];
      records.push({ ...banner, type__c: type });
      grouped.set(type, records);
    });
    const types = [
      ...this.typeOrder.filter((type) => grouped.has(type)),
      ...Array.from(grouped.keys()).filter(
        (type) => !this.typeOrder.includes(type),
      ),
    ];
    this.availableBanners = [];
    this.groups = types.map((type) => {
      const banners = grouped.get(type)!;
      const group = { type, banners, start: this.availableBanners.length };
      this.availableBanners.push(...banners);
      return group;
    });
    const matching = this.availableBanners
      .map((banner, index) => ({ banner, index }))
      .filter(
        ({ banner }) =>
          banner.type__c === previous?.type__c &&
          banner.content__c === previous?.content__c,
      );
    const previousGroup = this.groups.find(
      (group) => group.type === previous?.type__c,
    );
    this.activeIndex = resetSelection
      ? 0
      : (matching[occurrence]?.index ??
        (previousGroup
          ? previousGroup.start +
            Math.max(
              0,
              Math.min(previousPosition, previousGroup.banners.length - 1),
            )
          : 0));
    try {
      this.areaDismissed =
        this.dismissedLocations.has(this.currentLocation) ||
        !!SessionStorageUtility.get(
          `bannerAreaDismissed_${this.currentLocation}`,
        );
    } catch {
      // Retain the current in-memory dismissal if storage cannot be read.
    }
    if (panelFocused && this.availableBanners.length === 0) {
      this.restorePageFocus();
    } else if (focusedType && !grouped.has(focusedType)) {
      if (this.focusFrame !== null) {
        cancelAnimationFrame(this.focusFrame);
      }
      this.focusFrame = requestAnimationFrame(() => {
        this.focusFrame = null;
        this.elementRef.nativeElement
          .querySelector('[data-banner-type][aria-pressed="true"]')
          ?.focus();
      });
    }
  }

  private getDisplayLocationsForCurrentTab(): string[] {
    const displayLocations = TAB_TO_LOCATION_MAP[this.currentLocation] || [];
    if (
      this.currentLocation === DASHBOARD_TAB_CONSTANTS.CM &&
      CommonUtility.hasRecordType('Small Pack Brokerage')
    ) {
      return [...displayLocations, 'small-pack-brokerage'];
    }
    return displayLocations;
  }

  private generateBannerId(
    record: LocationBasedBannerRecord,
    index: number,
  ): string {
    return `location-banner-${record.type}-${index}-${this.hashContent(
      record.content,
    )}`;
  }

  private hashContent(content: string): string {
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  private getDismissedBannerIds(location: string): string[] {
    try {
      const dismissedKey = `bannerDismissed_${location}`;
      const dismissed = SessionStorageUtility.get(dismissedKey);
      return dismissed ? JSON.parse(dismissed) : [];
    } catch {
      return [];
    }
  }

  private clearAllDismissedFlags(): void {
    this.dismissedLocations.clear();
    this.areaDismissed = false;
    try {
      Object.values(DASHBOARD_TAB_CONSTANTS).forEach((tabKey) => {
        const dismissedKey = `bannerDismissed_${tabKey}`;
        SessionStorageUtility.clearItem(dismissedKey);
        SessionStorageUtility.clearItem(`bannerAreaDismissed_${tabKey}`);
      });
    } catch {
      // Silently fail
    }
  }

  setActiveLocation(location: string): void {
    if (this.currentLocation !== location) {
      this.currentLocation = location;
      this.areaDismissed = false;
      this.updateFilteredBanners([], true);
    }
  }

  getBannerTypeClass(type: string): string {
    return this.typeOrder.includes(type)
      ? `banner-${type.toLowerCase()}`
      : 'banner-neutral';
  }

  getVisibleBanners(): BannerItem[] {
    return this.banners.filter((banner) => !banner.dismissed);
  }

  trackByBannerId(index: number, banner: BannerItem): string {
    return banner.id || index.toString();
  }
}
